import { reportError } from '../errorManager';
import type { User } from '../../types/context.types';
import type { AnalyticsEventCode } from './eventCodes';
import type { AnalyticsProvider, IdentifyContext, PageViewPayload } from './types';
import {
  isAnalyticsCollectionEnabled,
  persistAnalyticsUserOptOut,
} from './analyticsGate';
import { COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS } from './consentFeatureFlags';
import { setConsentDecision } from './consentStore';

const extraProviders: AnalyticsProvider[] = [];

let analyticsInitStarted = false;
let analyticsReady = false;
let providersPromise: Promise<AnalyticsProvider[]> | null = null;
const pendingCalls: Array<(providers: AnalyticsProvider[]) => void> = [];

function loadProviders(): Promise<AnalyticsProvider[]> {
  if (!providersPromise) {
    providersPromise = Promise.all([
      import('./providers/mixpanel'),
      import('./providers/googleAnalytics'),
      import('./providers/googleAds'),
    ]).then(([mixpanel, googleAnalytics, googleAds]) => [
      mixpanel.mixpanelAnalyticsProvider,
      googleAnalytics.googleAnalyticsProvider,
      googleAds.googleAdsProvider,
      ...extraProviders,
    ]);
  }
  return providersPromise;
}

function enqueueOrRun(fn: (providers: AnalyticsProvider[]) => void): void {
  if (!analyticsReady) {
    pendingCalls.push(fn);
    return;
  }
  void loadProviders().then(fn);
}

function flushPending(providers: AnalyticsProvider[]): void {
  const queued = pendingCalls.splice(0);
  for (const fn of queued) {
    try {
      fn(providers);
    } catch (e) {
      reportError(e, { source: 'analytics', operation: 'flush_pending' });
    }
  }
}

function resetAllProviders(providers: AnalyticsProvider[]): void {
  for (const p of providers) {
    try {
      p.reset?.();
    } catch (e) {
      reportError(e, { source: 'analytics', operation: 'reset' });
    }
  }
}

export function registerAnalyticsProvider(provider: AnalyticsProvider): () => void {
  extraProviders.push(provider);
  providersPromise = null;
  analyticsReady = false;
  analyticsInitStarted = false;
  return () => {
    const i = extraProviders.indexOf(provider);
    if (i !== -1) extraProviders.splice(i, 1);
    providersPromise = null;
  };
}

/**
 * Loads provider SDKs. Must be called from deferred startup — tracking APIs
 * queue until this completes so Mixpanel/GA are not pulled into first paint.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!isAnalyticsCollectionEnabled()) return;
  if (analyticsInitStarted) return;
  analyticsInitStarted = true;

  void loadProviders().then((providers) => {
    for (const p of providers) {
      try {
        p.init?.();
      } catch (e) {
        reportError(e, { source: 'analytics', operation: 'provider_init' });
      }
    }
    analyticsReady = true;
    flushPending(providers);
  });
}

export function identifyUser(user: User | null, context?: IdentifyContext): void {
  if (!isAnalyticsCollectionEnabled()) return;
  enqueueOrRun((providers) => {
    for (const p of providers) {
      try {
        p.identify?.(user, context);
      } catch (e) {
        reportError(e, { source: 'analytics', operation: 'identify' });
      }
    }
  });
}

/** Always runs provider resets (e.g. logout) even when collection is disabled. */
export function resetUser(): void {
  if (!analyticsInitStarted) return;
  void loadProviders().then((providers) => resetAllProviders(providers));
}

export function trackPageView(pathname: string, user: User | null): void {
  if (!isAnalyticsCollectionEnabled()) return;
  const payload: PageViewPayload = { pathname, user };
  enqueueOrRun((providers) => {
    for (const p of providers) {
      try {
        p.trackPageView?.(payload);
      } catch (e) {
        reportError(e, { source: 'analytics', operation: 'trackPageView' });
      }
    }
  });
}

export function trackEvent(code: AnalyticsEventCode, properties?: Record<string, unknown>): void {
  if (!isAnalyticsCollectionEnabled()) return;
  enqueueOrRun((providers) => {
    for (const p of providers) {
      try {
        p.track?.(code, properties);
      } catch (e) {
        reportError(e, { source: 'analytics', operation: 'trackEvent' });
      }
    }
  });
}

/**
 * User-controlled opt-out (e.g. privacy settings). Persists to localStorage and resets providers when opting out.
 * When opting back in, re-inits providers and sends a page view for the current path.
 */
export function setAnalyticsUserOptOut(optOut: boolean): void {
  persistAnalyticsUserOptOut(optOut);
  if (optOut) {
    analyticsInitStarted = false;
    analyticsReady = false;
    if (providersPromise) {
      void loadProviders().then((providers) => resetAllProviders(providers));
    }
  } else {
    analyticsInitStarted = false;
    analyticsReady = false;
    void import('./providers/googleAnalytics').then(({ grantGaConsent }) => {
      grantGaConsent();
      initAnalytics();
    });
  }
}

/**
 * When strict consent is on, persists `accepted` and updates Consent Mode + providers.
 * When off, only updates Consent Mode + providers (no localStorage cookie key unless you enable strict).
 */
export function acceptAnalyticsConsent(): void {
  if (COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS) {
    setConsentDecision('accepted');
  }
  void import('./providers/googleAnalytics').then(({ grantGaConsent }) => {
    grantGaConsent();
    analyticsInitStarted = false;
    analyticsReady = false;
    initAnalytics();
    const payload: PageViewPayload = {
      pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
      user: null,
    };
    enqueueOrRun((providers) => {
      for (const p of providers) {
        try {
          p.trackPageView?.(payload);
        } catch (e) {
          reportError(e, { source: 'analytics', operation: 'consent_pageview' });
        }
      }
    });
  });
}

/** When strict consent is on, records decline. No provider init. */
export function declineAnalyticsConsent(): void {
  if (COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS) {
    setConsentDecision('declined');
  }
}
